import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Tooltip,
  SelectChangeEvent,
  SxProps,
  Theme
} from '@mui/material';
import gatewayService, { Gateway } from '../../services/gatewayService';

/** Valor do filtro "Sem Gateway" (apenas listagens; gateway_id IS NULL) */
export const GATEWAY_FILTER_SEM_VALUE = '__sem_gateway__';

interface GatewaySelectProps {
  value?: number | string;
  onChange: (gatewayId: number | null, gateway?: Gateway) => void;
  /** Chamado ao escolher "Sem Gateway" quando `includeSemGatewayOption` está ativo */
  onSemGatewayFilter?: () => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  showOnlyActive?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  sx?: SxProps<Theme>;
  /** Opção extra só para filtros: faturas sem gateway (gateway_id nulo) */
  includeSemGatewayOption?: boolean;
}

const GatewaySelect: React.FC<GatewaySelectProps> = ({
  value,
  onChange,
  label = 'Gateway de Pagamento',
  required = false,
  disabled = false,
  error = false,
  helperText,
  showOnlyActive = true,
  size = 'medium',
  fullWidth = true,
  variant = 'outlined',
  sx,
  includeSemGatewayOption = false,
  onSemGatewayFilter,
}) => {
  const [gateways, setGateways] = useState<Gateway[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Carregar gateways
  useEffect(() => {
    const loadGateways = async () => {
      try {
        setLoading(true);
        setErrorMessage(null);

        let gatewaysList: Gateway[];

        if (showOnlyActive) {
          gatewaysList = await gatewayService.getActiveGateways();
        } else {
          const response = await gatewayService.getAllGateways({ limit: 100 });
          gatewaysList = response.gateways;
        }

        setGateways(gatewaysList);
      } catch (error) {
        console.error('Erro ao carregar gateways:', error);
        setErrorMessage(error instanceof Error ? error.message : 'Erro ao carregar gateways');
        setGateways([]);
      } finally {
        setLoading(false);
      }
    };

    loadGateways();
  }, [showOnlyActive]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;

    if (selectedId === GATEWAY_FILTER_SEM_VALUE) {
      onSemGatewayFilter?.();
      return;
    }
    if (selectedId === '') {
      onChange(null);
    } else {
      const gatewayId = Number(selectedId);
      const selectedGateway = gateways.find(g => g.id === gatewayId);
      onChange(gatewayId, selectedGateway);
    }
  };

  const getGatewayDisplayName = (gateway: Gateway): string => {
    return `${gateway.nome}`;
  };

  const getGatewayStatusChip = (gateway: Gateway) => {
    if (!gateway.ativo) {
      return (
        <Chip
          label="Inativo"
          size="small"
          color="default"
          sx={{ ml: 1, height: 20 }}
        />
      );
    }

    if (gateway.status_conectividade === 'conectado') {
      return (
        <Chip
          label="Conectado"
          size="small"
          color="success"
          sx={{ ml: 1, height: 20 }}
        />
      );
    }

    if (gateway.status_conectividade === 'erro') {
      return (
        <Tooltip title={gateway.erro_conectividade || 'Erro de conectividade'}>
          <Chip
            label="Erro"
            size="small"
            color="error"
            sx={{ ml: 1, height: 20 }}
          />
        </Tooltip>
      );
    }

    return (
      <Chip
        label="Não testado"
        size="small"
        color="warning"
        sx={{ ml: 1, height: 20 }}
      />
    );
  };

  const getAmbienteChip = (gateway: Gateway) => {
    if (gateway.ambiente === 'sandbox') {
      return (
        <Chip
          label="Sandbox"
          size="small"
          color="warning"
          variant="outlined"
          sx={{ ml: 0.5, height: 20 }}
        />
      );
    }
    return (
      <Chip
        label="Produção"
        size="small"
        color="primary"
        variant="outlined"
        sx={{ ml: 0.5, height: 20 }}
      />
    );
  };

  if (errorMessage) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {errorMessage}
      </Alert>
    );
  }

  return (
    <FormControl
      fullWidth={fullWidth}
      size={size}
      variant={variant}
      error={error}
      disabled={disabled || loading}
      sx={sx}
    >
      <InputLabel id="gateway-select-label">
        {label}
        {required && ' *'}
      </InputLabel>

      <Select
        labelId="gateway-select-label"
        value={value?.toString() || ''}
        onChange={handleChange}
        label={label + (required ? ' *' : '')}
        disabled={disabled || loading}
        startAdornment={loading ? (
          <CircularProgress size={16} sx={{ mr: 1 }} />
        ) : null}
      >
        {/* Opção vazia para permitir não selecionar */}
        {!required && (
          <MenuItem value="">
            <Typography color="textSecondary">
              Nenhum gateway selecionado
            </Typography>
          </MenuItem>
        )}

        {includeSemGatewayOption && (
          <MenuItem value={GATEWAY_FILTER_SEM_VALUE}>
            <Typography color="text.secondary" fontWeight={600}>
              Sem Gateway
            </Typography>
          </MenuItem>
        )}

        {/* Evita o warning do MUI de valor fora do range durante o loading */}
        {value && !gateways.some(g => g.id.toString() === value.toString()) && value.toString() !== GATEWAY_FILTER_SEM_VALUE && (
          <MenuItem value={value.toString()} sx={{ display: 'none' }}>
            {value}
          </MenuItem>
        )}

        {gateways.map((gateway) => (
          <MenuItem key={gateway.id} value={gateway.id.toString()}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
              <Typography sx={{ flexGrow: 1 }}>
                <img src={`/logo-bancos/icones/${gatewayService.getGatewayLogoIcon(gateway.tipo)}`} style={{ width: 20, height: 12 }} /> - {getGatewayDisplayName(gateway)}
              </Typography>

              {getAmbienteChip(gateway)}
            </Box>
          </MenuItem>
        ))}

        {gateways.length === 0 && !loading && (
          <MenuItem disabled>
            <Typography color="textSecondary">
              Nenhum gateway disponível
            </Typography>
          </MenuItem>
        )}
      </Select>

      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'textSecondary'} sx={{ mt: 0.5, ml: 1.5, fontSize: '0.75rem' }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default GatewaySelect; 